import {
  type Tool as AuiTool,
  type ToolCallMessagePartComponent,
  type ToolCallMessagePartProps,
  useAui,
} from '@assistant-ui/react';
import { use, useEffect } from 'react';
import z from 'zod';

import { BindingScopeContext } from '#core/lib/expr/session/BindingScopeContext.ts';
import type { MantineColor } from '#core/lib/formatters/colors.ts';

import { ToolDisplay, type ToolDisplayProps } from '#plugin-chat/components/tool-display/ToolDisplay.tsx';
import { ToolsContext, type ToolsRefs } from '#plugin-chat/contexts/ToolsContext.ts';
import { renderInstructions } from '#plugin-chat/lib/instructions/reconciler.ts';
import { type ToolErrorResponse, toToolErrorResponse } from '#plugin-chat/lib/tool/errors/index.ts';
import type {
  AnyToolLabelValues,
  AnyToolStatusRenderers,
  StyledIcon,
  ToolLabelValues,
  ToolMessageFormatters,
  ToolStatusRenderers,
} from '#plugin-chat/lib/tool/message-formatters.ts';
import { RevisionBoundary } from '#plugin-chat/lib/tool/RevisionBoundary.tsx';
import { parseResultEnvelope, type ResultEnvelope } from '#plugin-chat/lib/tool/result-envelope.ts';
import {
  forcedReplacementTarget,
  type RevisionScanMessage,
  resolveReplacesArg,
} from '#plugin-chat/lib/tool/revisions.ts';
import {
  DEFAULT_REPEAT_SIGNATURE_THRESHOLD,
  failureSignature,
  flagRepeatedFailure,
  hasPriorIdenticalFailure,
  priorFailureSignatureCount,
  type ThreadMessageLike,
  type ToolArgsIdentity,
  toolCallIdentity,
} from '#plugin-chat/lib/tool/tool-identity.ts';
import { useBindingScope } from '#plugin-chat/session/useBindingScope.ts';

/**
 * A tool's own `execute` implementation. Receives the LLM-provided args
 * (with the synthetic `replaces` revision parameter already stripped for
 * revisable tools) plus whatever trailing arguments the runtime passes
 * through, and produces the tool's result envelope (sync or async).
 *
 * The trailing runtime arguments are forwarded opaquely — `never[]` lets
 * an implementation declare its own typed view of them (e.g. the
 * human-in-the-loop `{ human }` handle) while `runToolCall` forwards
 * whatever the runtime supplied.
 */
type ToolExecute<ArgsType extends Record<string, unknown>, ResultType> = (
  args: ArgsType,
  ...rest: never[]
) => ResultType | Promise<ResultType>;

/**
 * The thread-message shape the execute pipeline scans: the revision
 * index's structural slice intersected with the repeated-failure guard's,
 * so one messages getter serves both consumers.
 */
type ScannableMessage = RevisionScanMessage & ThreadMessageLike;

/**
 * Frame mounted around every registered tool render, providing the
 * contexts and chrome the render's subtree needs across the `setToolUI`
 * handoff:
 *
 * - {@link ToolsContext}: assistant-ui mounts registered components inside
 *   the thread viewport — a sibling of the registration subtree, where
 *   AtlasAssistant's provider is not an ancestor — so the registry
 *   captured at the registration site must be re-provided here. Without
 *   this bridge, `use(ToolsContext)` in a tool render resolves the
 *   tripwire default and every wire-name comparison silently fails.
 * - {@link BindingScopeContext}: resolves cross-turn `(col <name> …)`
 *   references. The cross-turn dataset namespace is a pure projection of
 *   the active message branch; `useBindingScope()` derives the resolver
 *   for *this* tool-call part's position (every producer visible strictly
 *   before it, including ones still streaming). assistant-ui renders the
 *   registered component once per tool-call part inside message/part
 *   scope, so the hook reads the correct position. There is no global
 *   hydration gate — each tool self-resolves on render.
 * - {@link RevisionBoundary}: revision-aware treatment (supersede link /
 *   history stepper) derived from the thread at render time.
 */
function ToolPartFrame<ArgsType extends Record<string, unknown>, ResultType>({
  tools,
  Render,
  partProps,
}: {
  tools: ToolsRefs;
  Render: ToolCallMessagePartComponent<ArgsType, ResultType>;
  partProps: ToolCallMessagePartProps<ArgsType, ResultType>;
}) {
  return (
    <ToolsContext value={tools}>
      <BindingScopeContext value={useBindingScope()}>
        <RevisionBoundary partProps={partProps} Render={Render} />
      </BindingScopeContext>
    </ToolsContext>
  );
}

/**
 * The execute pipeline every `<Tool>` call flows through — the single,
 * mandatory choke point; no tool may bypass it. One linear sequence:
 *
 * 1. Revision boundary. Resolving a model-provided `replaces` revision id
 *    happens BEFORE execute — an unresolvable reference must fail the call
 *    outright (`resolveReplacesArg` throws into the error envelope below)
 *    rather than render a result with broken lineage. The resolved — or
 *    flow-signal-forced (`forcedReplacementTarget`) — target is stamped
 *    onto a successful result as `replaces`, the edge the transcript and
 *    UI collapse superseded calls by.
 * 2. Error envelope. A thrown `execute` must reach the transcript as the
 *    structured `{ success: false, … }` envelope, otherwise the raw error
 *    lands on the tool part with no failure marker, is misclassified as a
 *    success by `parseResultEnvelope`, and is silently dropped — the LLM
 *    never learns the tool failed and blindly re-issues the same call.
 * 3. Repeated-failure guard. The call always runs first — we never
 *    pre-emptively refuse. Once it has FAILED, the step-back directive is
 *    prepended if either (a) an identical call (same tool, same normalised
 *    args) already failed on this thread, or (b) the same failure
 *    SIGNATURE has recurred enough times across lightly-varied calls — the
 *    latter catches retry loops that mutate the call without addressing
 *    the cause, which the exact-AST identity misses. The in-flight call's
 *    own part is not yet on the thread, so the counts already exclude it.
 */
const runToolCall = async <ArgsType extends Record<string, unknown>, ResultType>(params: {
  name: string;
  execute: ToolExecute<ArgsType, ResultType>;
  revisable: boolean | undefined;
  identityKey: ToolArgsIdentity | undefined;
  getMessages: () => readonly ScannableMessage[];
  execArgs: readonly unknown[];
  errorResponseProps?: (() => ToolErrorResponse | Record<string, unknown>) | undefined;
}): Promise<unknown> => {
  const { name, execute, revisable, identityKey, getMessages, execArgs, errorResponseProps } = params;
  // Envelope discrimination happens ONCE, at the boundary that produced the
  // value — both the `replaces` graft and the repeated-failure guard below
  // consume the same parsed result.
  const { result, settled } = await (async (): Promise<{ result: unknown; settled: ResultEnvelope }> => {
    try {
      const messages = getMessages();
      const args = (execArgs[0] ?? {}) as Record<string, unknown>;
      const cited = revisable && typeof args.replaces === 'string' ? args.replaces : undefined;
      const resolved = cited === undefined ? undefined : resolveReplacesArg(messages, name, cited).toolCallId;
      const { replaces: _cited, ...bareArgs } = args;
      // The trailing runtime args are forwarded opaquely — see ToolExecute.
      const output = await execute((revisable ? bareArgs : execArgs[0]) as ArgsType, ...(execArgs.slice(1) as never[]));
      const envelope = parseResultEnvelope(output);
      const replaces = resolved ?? forcedReplacementTarget(messages, name);
      const graftable = envelope.kind === 'success' && typeof output === 'object' && output !== null;
      return {
        result: replaces !== undefined && graftable ? { ...output, replaces } : output,
        settled: envelope,
      };
    } catch (err) {
      console.error(`[Tool] execute threw for "${name}" — normalising to error envelope`, err);
      const failure = { ...toToolErrorResponse(err), ...(errorResponseProps?.() ?? {}) };
      return { result: failure, settled: parseResultEnvelope(failure) };
    }
  })();

  if (settled.kind !== 'failure') {
    return result;
  }
  const messages = getMessages();
  const repeated =
    hasPriorIdenticalFailure(messages, name, toolCallIdentity(name, execArgs[0], identityKey), identityKey) ||
    priorFailureSignatureCount(messages, failureSignature(name, settled.error)) >= DEFAULT_REPEAT_SIGNATURE_THRESHOLD;
  return repeated ? flagRepeatedFailure(settled.error) : result;
};

export function Tool<ArgsType extends Record<string, unknown>, ResultType>({
  name,
  render,
  color,
  icon,
  sticky,
  labelValues,
  description,
  parameters,
  execute,
  message,
  identityKey,
  revisable,
  collapsible,
  errorResponseProps,
  children,
}: React.PropsWithChildren<{
  name: string;
  /**
   * Per-status render overrides for the tool-display chrome. Statuses
   * without an override render the default conventional status row
   * (icon + i18n label from `tools.<name>.<status>`).
   */
  render?: ToolStatusRenderers<ArgsType, ResultType>;
  /** Tool color: derives the default complete-row color. */
  color?: MantineColor;
  /** Default complete-row icon. */
  icon?: StyledIcon;
  /** Pin the tool chrome to the viewport top while its content scrolls. */
  sticky?: boolean;
  /** i18n interpolation values for the conventional status/progress labels. */
  labelValues?: ToolLabelValues<ArgsType, ResultType>;
  message?: ToolMessageFormatters<ArgsType, ResultType>;
  description?: React.ReactNode;
  parameters: z.ZodType;
  disabled?: boolean;
  execute: ToolExecute<ArgsType, ResultType>;
  /**
   * Optional args normaliser for the repeated-failure guard. Tools that
   * carry an s-expression pass the shared `expressionArgsIdentity` so
   * formatting-only differences don't read as a "new" call. Omitted ⇒
   * args are hashed as-is.
   */
  identityKey?: ToolArgsIdentity;
  /**
   * Opt the tool into revision tracking (see `lib/tool/revisions.ts`).
   * The input schema gains an optional `replaces` revision-id parameter,
   * successful results state their citable revision id in the transcript,
   * and a model-provided `replaces` is resolved (hard failure when
   * unresolvable) and stamped onto the result as the superseding edge.
   */
  revisable?: boolean;
  /**
   * Fold consecutive calls of this tool into the collapsed tool-run
   * accordion. Declare on background/mechanical tools; leave off display
   * tools whose output is the deliverable.
   */
  collapsible?: boolean;
  errorResponseProps?: (() => ToolErrorResponse | Record<string, unknown>) | undefined;
}>) {
  const aui = useAui();
  const toolDescription = renderInstructions(description);
  // Captured here — in the registration tree, where AtlasAssistant's
  // provider is an ancestor — and bridged into the registered render by
  // ToolPartFrame.
  const tools = use(ToolsContext);

  useEffect(() => {
    // Erasure boundary: the chrome consumes the renderers/labelValues with
    // the per-tool generics forgotten, mirroring the sidecar read-side cast.
    const Render: ToolCallMessagePartComponent<ArgsType, ResultType> = (props) => (
      <ToolDisplay
        color={color}
        icon={icon}
        interrupt={props.interrupt}
        labelValues={labelValues as AnyToolLabelValues | undefined}
        renderers={render as AnyToolStatusRenderers | undefined}
        resume={props.resume as ToolDisplayProps['resume']}
        sticky={sticky}
      />
    );
    const RegisteredRender: ToolCallMessagePartComponent<ArgsType, ResultType> = (props) => (
      <ToolPartFrame partProps={props} Render={Render} tools={tools} />
    );
    const unsetToolUi = aui.tools().setToolUI(name, RegisteredRender);

    const schema =
      revisable && parameters instanceof z.ZodObject
        ? parameters.extend({
            replaces: z
              .string()
              .optional()
              .describe(
                `Revision id of an earlier ${name} result this call should replace. Only set it if this call should replace a previously rendered result with specific changes, and only set it when you've been provided with an exact ID for replacement. The old version collapses into this one as revision history. Omit \`replaces\` by default, and when producing a new/unrelated result.`,
              ),
          })
        : parameters;

    const unregisterToolEvent = aui.modelContext().register({
      getModelContext: () => ({
        tools: {
          [name]: {
            description: toolDescription,
            parameters: schema.toJSONSchema({
              cycles: 'throw',
              reused: 'inline',
              target: 'openapi-3.0',
              unrepresentable: 'throw',
              io: 'input',
            }),
            execute: (...execArgs: unknown[]) =>
              runToolCall({
                name,
                execute,
                revisable,
                identityKey,
                getMessages: () => aui.thread().getState().messages as readonly ScannableMessage[],
                execArgs,
                errorResponseProps,
              }),
            // Sidecar metadata (`ToolSidecarEntry`) the transcript builder
            // and chrome read back off the model-context entry; none of it
            // is part of assistant-ui's `Tool` contract. The entry is erased
            // to `Tool` here, mirrored by the typed cast `useToolsMap`
            // applies on the read side.
            message,
            labelValues,
            revisable,
            collapsible,
          } as unknown as AuiTool,
        },
      }),
    });

    return () => {
      unregisterToolEvent();
      unsetToolUi();
    };
  }, [
    aui,
    name,
    render,
    color,
    icon,
    sticky,
    labelValues,
    toolDescription,
    parameters,
    execute,
    message,
    identityKey,
    revisable,
    collapsible,
    tools,
    errorResponseProps,
  ]);

  return children;
}
